import React, { useEffect, Children, createElement } from 'react';
import { Link } from 'react-router-dom';
import { Sun, Users, Shield, Heart, Activity, Trophy, CheckCircle2, Phone, Calendar, Clock, MapPin, AlertCircle, Sparkles, Zap, MessageSquare, Shirt, Mail, Infinity as InfinityIcon, Wallet } from 'lucide-react';
import { SocialProofBar } from '../components/SocialProofBar';
import { LeadForm } from '../components/LeadForm';
import { ReviewCard } from '../components/ReviewCard';
import { LocationBlock } from '../components/LocationBlock';
import { FAQAccordion } from '../components/FAQAccordion';
import { CTABand } from '../components/CTABand';
// === EDITABLE CAMP CONFIG (2026 Season) ===
const CAMP_SEASON = {
  year: '2026',
  theme: 'Unleash Your Inner Storm Warrior',
  hours: 'Mon–Fri, 10:00 AM – 4:00 PM',
  ages: 'Ages 6 and up',
  registrationPhone: '517-225-9501',
  registrationEmail: 'megan@stormtaekwondo.com',
  mainPhone: '517-220-0621'
};
const WEEKLY_SESSIONS = [{
  week: 1,
  dates: 'June 22 – June 26, 2026',
  short: 'Jun 22 – 26',
  days: 'Mon–Fri'
}, {
  week: 2,
  dates: 'July 13 – July 17, 2026',
  short: 'Jul 13 – 17',
  days: 'Mon–Fri'
}, {
  week: 3,
  dates: 'August 3 – August 7, 2026',
  short: 'Aug 3 – 7',
  days: 'Mon–Fri'
}, {
  week: 4,
  dates: 'August 17 – August 21, 2026',
  short: 'Aug 17 – 21',
  days: 'Mon–Fri'
}];
const PRICING = [{
  title: 'One-Week Session',
  price: '$200',
  sub: 'per week',
  copy: 'Standard rate for new / non-enrolled students.'
}, {
  title: 'Additional Child',
  price: '$100',
  sub: 'per week',
  copy: 'For each additional child in the same family.'
}, {
  title: 'Enrolled Student',
  price: '$150',
  sub: 'per week',
  copy: 'Discounted rate for currently enrolled Storm students.'
}, {
  title: 'Enrolled Additional Child',
  price: '$75',
  sub: 'per week',
  copy: 'For each additional child of an enrolled student.'
}];
const TRUST_BADGES = ['Ages 6+', 'Weekly Sessions', 'Mon–Fri Camp', 'Shirts Included', 'Pay Weekly or Daily', 'No Capacity Limit', 'Farmington Hills'];
const BENEFITS = [{
  icon: Users,
  title: 'Social Skills Development',
  copy: 'Kids build interaction, sharing and teamwork skills in a structured group environment.'
}, {
  icon: Heart,
  title: 'Confidence & Independence',
  copy: 'Camp helps kids gain independence, confidence and comfort working with peers.'
}, {
  icon: Shield,
  title: 'Safe & Supervised Environment',
  copy: 'Children are cared for in a secure, organized setting led by the Storm Taekwondo team.'
}, {
  icon: Trophy,
  title: 'Fun Taekwondo Training',
  copy: 'Taekwondo activities help kids build discipline, focus, coordination and confidence.'
}, {
  icon: Activity,
  title: 'Fitness & Movement',
  copy: 'Kids move, stretch, train and stay active during the summer instead of sitting all day.'
}, {
  icon: CheckCircle2,
  title: 'Dependable Daily Structure',
  copy: 'Parents get a reliable camp option while kids stay active, supervised and engaged.'
}];
const ACTIVITIES = ['Taekwondo training', 'Fitness games', 'Team-building activities', 'Confidence challenges', 'Self-defense basics', 'Stretching and movement', 'Forms and kicking drills', 'Lunch and snack breaks'];
const WHAT_TO_BRING = ['Packed lunch', 'Snack', 'Water bottle', 'Comfortable clothes', 'Positive attitude'];
const WHY_STORM = [{
  icon: Calendar,
  title: 'Structured Environment',
  copy: 'Predictable, organized daily schedule kids can rely on.'
}, {
  icon: Sun,
  title: 'Active Summer Schedule',
  copy: 'Kids stay moving instead of sitting in front of screens.'
}, {
  icon: Trophy,
  title: 'Martial Arts Confidence',
  copy: 'Real Taekwondo skills taught by experienced coaches.'
}, {
  icon: Shield,
  title: 'Supervised Group Setting',
  copy: 'Trained Storm staff oversee every camp session.'
}, {
  icon: Users,
  title: 'Teamwork & Social Skills',
  copy: 'Group activities help kids make friends and collaborate.'
}, {
  icon: MapPin,
  title: 'Located in Farmington Hills',
  copy: 'Conveniently located on Crestview for local families.'
}];
const FAQS = [{
  question: 'How do I register my child for summer camp?',
  answer: 'The easiest way to register is to text or call Megan directly at 517-225-9501. You can also email megan@stormtaekwondo.com to get started.'
}, {
  question: 'Is there a capacity limit?',
  answer: 'No set limit. If more campers sign up, additional helpers will be added to keep the group well-supervised. Reach out early to lock in your spot.'
}, {
  question: 'What age is Storm Summer Camp for?',
  answer: 'Camp is open to kids ages 6 and up.'
}, {
  question: 'What time is summer camp?',
  answer: 'Camp runs Monday through Friday from 10:00 AM to 4:00 PM.'
}, {
  question: 'Can my child be dropped off early?',
  answer: 'No, there is no early drop-off. Please plan to arrive at the start of the camp day at 10:00 AM.'
}, {
  question: 'What if I am running late for pickup?',
  answer: 'Late pickup is fine. Campers can hang out until parents arrive, and Storm holds regular classes immediately after camp ends.'
}, {
  question: 'How does payment work? Can I pay by the day?',
  answer: 'You pay one week at a time, but there is also an option to pay by the day. Text or call 517-225-9501 to discuss what works best for your family.'
}, {
  question: 'Are camp shirts included?',
  answer: 'Yes! A Storm Taekwondo camp shirt is included for every camper.'
}, {
  question: 'What should my child bring?',
  answer: 'Campers should bring their own lunch, snack, water bottle, and wear comfortable clothes.'
}, {
  question: 'Does my child need Taekwondo experience?',
  answer: 'No experience required. Camp is designed to help kids stay active, learn, and participate in structured martial arts activities at any level.'
}, {
  question: 'Where is the camp located?',
  answer: 'Storm Taekwondo is located at 24786 Crestview, Farmington Hills, MI 48335.'
}];
export function SummerCamp() {
  useEffect(() => {
    document.title = 'Storm Taekwondo Summer Camp 2026 | Farmington Hills, MI';
    const metaDesc = document.querySelector('meta[name="description"]');
    const content = 'Storm Taekwondo Martial Arts Summer Camp 2026 in Farmington Hills. Weekly sessions for ages 6+, Mon–Fri 10–4. Shirts included, pay weekly or daily. Text 517-225-9501 to register.';
    if (metaDesc) {
      metaDesc.setAttribute('content', content);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = content;
      document.head.appendChild(meta);
    }
  }, []);
  return <div className="flex flex-col bg-off-white" data-id="element-766">
      {/* === HERO === */}
      <section className="relative bg-ink pt-12 pb-24 lg:pt-20 lg:pb-32 overflow-hidden" data-id="element-767">
        <div className="absolute inset-0 opacity-25" data-id="element-768">
          <img src="https://cdn.magicpatterns.com/uploads/jpxHBCPtqv9Pv8vdpeiuPD/Family.png" alt="Storm Taekwondo summer camp in Farmington Hills" className="w-full h-full object-cover" data-id="element-769" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/90 to-transparent" data-id="element-770" />
        </div>

        <div className="absolute top-0 right-0 w-1/3 h-full bg-storm-red transform skew-x-12 translate-x-32 opacity-10 pointer-events-none" data-id="element-771" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-772">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-773">
            <div className="flex flex-col items-start" data-id="element-774">
              <div className="inline-flex items-center gap-2 bg-storm-red text-white px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wide mb-6" data-id="element-775">
                <Sun className="w-4 h-4" data-id="element-776" />
                Summer Camp {CAMP_SEASON.year}
              </div>

              <div className="mb-8" data-id="element-777">
                <SocialProofBar data-id="element-778" />
              </div>

              <h1 className="text-5xl md:text-6xl text-white mb-4 leading-[1.05]" data-id="element-779">
                Martial Arts <span className="text-storm-red" data-id="element-780">Summer Camp</span>{' '}
                in Farmington Hills
              </h1>

              <p className="font-display text-2xl md:text-3xl text-gold mb-6 tracking-wide uppercase" data-id="element-781">
                {CAMP_SEASON.theme}
              </p>

              <p className="text-xl text-off-white mb-8 max-w-xl leading-relaxed" data-id="element-782">
                Join us for an exciting summer of Taekwondo, fun activities, and
                personal growth! Storm Taekwondo's martial arts camp gives kids
                a safe place to stay active, make friends and build life skills.
              </p>

              <ul className="flex flex-col gap-3 mb-10" data-id="element-783">
                {['Weekly sessions, June through August', 'Ages 6 and up', 'Monday–Friday, 10:00 AM – 4:00 PM', 'Storm Taekwondo shirt included'].map((item, idx) => <li key={idx} className="flex items-center gap-3 text-white text-lg" data-id="element-784">
                    <CheckCircle2 className="w-6 h-6 text-storm-red shrink-0" data-id="element-785" />
                    <span data-id="element-786">{item}</span>
                  </li>)}
              </ul>

              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto" data-id="element-787">
                <a href={`sms:${CAMP_SEASON.registrationPhone.replace(/-/g, '')}`} className="btn-primary" data-id="element-788">
                  Text To Register
                </a>
                <a href={`tel:${CAMP_SEASON.registrationPhone}`} className="btn-secondary" data-id="element-789">
                  <Phone className="w-5 h-5 mr-2" data-id="element-790" />
                  Call {CAMP_SEASON.registrationPhone}
                </a>
              </div>
            </div>

            {/* Registration Card */}
            <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto" data-id="element-791">
              <div className="bg-paper p-8 rounded-2xl shadow-2xl border border-subtle-gray relative" data-id="element-792">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-storm-red text-white px-6 py-2 rounded-full font-bold uppercase tracking-wide whitespace-nowrap shadow-lg text-sm" data-id="element-793">
                  Register Today
                </div>

                <div className="mt-4" data-id="element-794">
                  <h2 className="text-3xl text-ink text-center mb-2" data-id="element-795">
                    Text or Call Megan
                  </h2>
                  <p className="text-gray-warm text-center text-sm mb-6" data-id="element-796">
                    The fastest way to lock in a camp spot is to text or call
                    directly. Megan will confirm dates, pricing and any details.
                  </p>

                  <a href={`tel:${CAMP_SEASON.registrationPhone}`} className="block bg-storm-red hover:bg-storm-red-dark transition-colors text-white p-6 rounded-xl mb-3 group" data-id="element-797">
                    <div className="flex items-center gap-3 mb-1" data-id="element-798">
                      <Phone className="w-5 h-5" data-id="element-799" />
                      <span className="font-bold text-sm uppercase tracking-wide" data-id="element-800">
                        Call
                      </span>
                    </div>
                    <p className="font-display text-3xl tracking-wide" data-id="element-801">
                      {CAMP_SEASON.registrationPhone}
                    </p>
                  </a>

                  <a href={`sms:${CAMP_SEASON.registrationPhone.replace(/-/g, '')}`} className="block bg-ink hover:bg-charcoal transition-colors text-white p-6 rounded-xl mb-3" data-id="element-802">
                    <div className="flex items-center gap-3 mb-1" data-id="element-803">
                      <MessageSquare className="w-5 h-5 text-storm-red" data-id="element-804" />
                      <span className="font-bold text-sm uppercase tracking-wide" data-id="element-805">
                        Text
                      </span>
                    </div>
                    <p className="font-display text-3xl tracking-wide" data-id="element-806">
                      {CAMP_SEASON.registrationPhone}
                    </p>
                  </a>

                  <a href={`mailto:${CAMP_SEASON.registrationEmail}`} className="block bg-off-white hover:bg-subtle-gray transition-colors text-ink p-4 rounded-xl border border-subtle-gray" data-id="element-807">
                    <div className="flex items-center gap-3" data-id="element-808">
                      <Mail className="w-5 h-5 text-storm-red" data-id="element-809" />
                      <div data-id="element-810">
                        <span className="font-bold text-xs uppercase tracking-wide text-gray-warm block" data-id="element-811">
                          Email
                        </span>
                        <p className="font-bold text-sm text-ink break-all" data-id="element-812">
                          {CAMP_SEASON.registrationEmail}
                        </p>
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === TRUST BAR === */}
      <section className="bg-charcoal py-6 border-y border-storm-red/20" data-id="element-813">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-814">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3" data-id="element-815">
            {TRUST_BADGES.map((badge, idx) => <div key={idx} className="flex items-center gap-2 text-white text-sm font-bold" data-id="element-816">
                <Zap className="w-4 h-4 text-storm-red" fill="currentColor" data-id="element-817" />
                <span className="uppercase tracking-wide" data-id="element-818">{badge}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* === KEY INFO STRIP === */}
      <section className="py-16 bg-paper border-b border-subtle-gray" data-id="element-819">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-820">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-821">
            <div className="bg-off-white p-6 rounded-2xl border border-subtle-gray" data-id="element-822">
              <Shirt className="w-8 h-8 text-storm-red mb-3" data-id="element-823" />
              <h3 className="text-lg text-ink mb-1" data-id="element-824">Camp Shirt Included</h3>
              <p className="text-gray-warm text-sm" data-id="element-825">
                Every camper gets an official Storm Taekwondo shirt.
              </p>
            </div>
            <div className="bg-off-white p-6 rounded-2xl border border-subtle-gray" data-id="element-826">
              <Wallet className="w-8 h-8 text-storm-red mb-3" data-id="element-827" />
              <h3 className="text-lg text-ink mb-1" data-id="element-828">Flexible Payment</h3>
              <p className="text-gray-warm text-sm" data-id="element-829">
                Pay one week at a time, or ask about paying by the day.
              </p>
            </div>
            <div className="bg-off-white p-6 rounded-2xl border border-subtle-gray" data-id="element-830">
              <InfinityIcon className="w-8 h-8 text-storm-red mb-3" data-id="element-831" />
              <h3 className="text-lg text-ink mb-1" data-id="element-832">No Capacity Limit</h3>
              <p className="text-gray-warm text-sm" data-id="element-833">
                If more campers sign up, more helpers are added.
              </p>
            </div>
            <div className="bg-off-white p-6 rounded-2xl border border-subtle-gray" data-id="element-834">
              <Clock className="w-8 h-8 text-storm-red mb-3" data-id="element-835" />
              <h3 className="text-lg text-ink mb-1" data-id="element-836">Late Pickup OK</h3>
              <p className="text-gray-warm text-sm" data-id="element-837">
                Kids can hang out. Regular classes start right after camp.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* === BENEFITS === */}
      <section className="py-24 bg-off-white" data-id="element-838">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-839">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-840">
            <h2 className="text-4xl md:text-5xl text-ink mb-6" data-id="element-841">
              A Summer Camp That Keeps Kids Active, Engaged & Growing
            </h2>
            <p className="text-xl text-gray-warm" data-id="element-842">
              More than just childcare. A structured experience that builds real
              skills.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-843">
            {BENEFITS.map((benefit, idx) => <div key={idx} className="bg-paper p-8 rounded-2xl border border-subtle-gray hover:border-storm-red/40 hover:shadow-lg transition-all" data-id="element-844">
                <div className="w-14 h-14 bg-storm-red/10 rounded-xl flex items-center justify-center mb-6 text-storm-red transform -skew-x-6" data-id="element-845">
                  <benefit.icon className="w-7 h-7 transform skew-x-6" data-id="element-846" />
                </div>
                <h3 className="text-2xl mb-3 text-ink" data-id="element-847">{benefit.title}</h3>
                <p className="text-gray-warm leading-relaxed" data-id="element-848">{benefit.copy}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* === WHAT KIDS DO === */}
      <section className="py-24 bg-paper border-y border-subtle-gray" data-id="element-849">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-850">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-851">
            <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/5]" data-id="element-852">
              <img src="https://cdn.magicpatterns.com/uploads/jpxHBCPtqv9Pv8vdpeiuPD/Family.png" alt="Kids participating in Storm Taekwondo summer camp" className="w-full h-full object-cover" data-id="element-853" />
              <div className="absolute bottom-6 left-6 bg-storm-red text-white px-4 py-2 rounded-full font-bold uppercase tracking-wide text-sm shadow-lg" data-id="element-854">
                <Sparkles className="w-4 h-4 inline mr-2" data-id="element-855" />
                Active Days
              </div>
            </div>

            <div data-id="element-856">
              <h2 className="text-4xl md:text-5xl text-ink mb-6" data-id="element-857">
                What Kids Do at Storm Summer Camp
              </h2>
              <p className="text-lg text-gray-warm mb-8 leading-relaxed" data-id="element-858">
                Each day is filled with a balanced mix of martial arts training,
                fitness fun, teamwork and rest. Kids stay active without burning
                out.
              </p>

              <div className="grid grid-cols-2 gap-3" data-id="element-859">
                {ACTIVITIES.map((activity, idx) => <div key={idx} className="flex items-center gap-3 bg-off-white p-4 rounded-xl border border-subtle-gray" data-id="element-860">
                    <div className="w-8 h-8 bg-storm-red rounded-lg flex items-center justify-center shrink-0" data-id="element-861">
                      <CheckCircle2 className="w-5 h-5 text-white" data-id="element-862" />
                    </div>
                    <span className="text-ink font-bold text-sm" data-id="element-863">
                      {activity}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === WEEKLY SESSIONS (2026 DATES) === */}
      <section id="weekly-sessions" className="py-24 bg-off-white" data-id="element-864">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-865">
          <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-866">
            <div className="inline-flex items-center gap-2 bg-storm-red/10 text-storm-red px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wide mb-4" data-id="element-867">
              <Calendar className="w-4 h-4" data-id="element-868" />
              {CAMP_SEASON.year} Schedule
            </div>
            <h2 className="text-4xl md:text-5xl text-ink mb-6" data-id="element-869">
              Camp Dates & Weekly Sessions
            </h2>
            <p className="text-xl text-gray-warm" data-id="element-870">
              Choose the week (or weeks) that work best for your family. Text or
              call to lock in a spot.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-871">
            {WEEKLY_SESSIONS.map(session => <div key={session.week} className="bg-paper rounded-2xl p-6 border border-subtle-gray hover:border-storm-red hover:shadow-xl transition-all flex flex-col" data-id="element-872">
                <div className="flex items-center justify-between mb-4" data-id="element-873">
                  <span className="font-display text-2xl text-storm-red" data-id="element-874">
                    Week {session.week}
                  </span>
                  <span className="text-xs font-bold text-gray-warm uppercase tracking-wide bg-off-white px-3 py-1 rounded-full border border-subtle-gray" data-id="element-875">
                    {session.days}
                  </span>
                </div>
                <div className="mb-6 flex-grow" data-id="element-876">
                  <p className="text-xl font-bold text-ink mb-2 leading-tight" data-id="element-877">
                    {session.short}
                  </p>
                  <p className="text-sm text-gray-warm mb-1" data-id="element-878">
                    {session.dates.split(', ')[1]}
                  </p>
                  <p className="text-sm text-gray-warm" data-id="element-879">{CAMP_SEASON.hours}</p>
                </div>
                <a href={`tel:${CAMP_SEASON.registrationPhone}`} className="btn-primary w-full text-center text-base py-3" data-id="element-880">
                  Reserve Week {session.week}
                </a>
              </div>)}
          </div>

          <div className="max-w-3xl mx-auto bg-ink text-white rounded-2xl p-8 text-center" data-id="element-881">
            <p className="text-lg mb-4" data-id="element-882">
              <span className="font-bold" data-id="element-883">Ready to sign up?</span> Text or call
              Megan to reserve your child's spot.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-884">
              <a href={`tel:${CAMP_SEASON.registrationPhone}`} className="btn-primary" data-id="element-885">
                <Phone className="w-5 h-5 mr-2" data-id="element-886" />
                Call {CAMP_SEASON.registrationPhone}
              </a>
              <a href={`sms:${CAMP_SEASON.registrationPhone.replace(/-/g, '')}`} className="btn-outline border-white text-white hover:bg-white hover:text-ink" data-id="element-887">
                <MessageSquare className="w-5 h-5 mr-2" data-id="element-888" />
                Text To Register
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* === PRICING === */}
      <section className="py-24 bg-paper border-y border-subtle-gray" data-id="element-889">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-890">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-891">
            <h2 className="text-4xl md:text-5xl text-ink mb-6" data-id="element-892">
              Summer Camp Pricing
            </h2>
            <p className="text-xl text-gray-warm" data-id="element-893">
              Simple weekly pricing. Pay by the week, or ask about paying by the
              day.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-894">
            {PRICING.map((tier, idx) => {
            const isEnrolled = idx >= 2;
            return <div key={idx} className={`relative rounded-2xl p-8 border-2 transition-all ${isEnrolled ? 'bg-ink border-storm-red text-white shadow-xl' : 'bg-off-white border-subtle-gray hover:border-storm-red/40 hover:shadow-lg'}`} data-id="element-895">
                  {isEnrolled && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-ink px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide whitespace-nowrap" data-id="element-896">
                      Storm Member
                    </div>}
                  <h3 className={`text-xl mb-4 ${isEnrolled ? 'text-white' : 'text-ink'}`} data-id="element-897">
                    {tier.title}
                  </h3>
                  <p className={`font-display text-5xl mb-1 ${isEnrolled ? 'text-storm-red' : 'text-ink'}`} data-id="element-898">
                    {tier.price}
                  </p>
                  <p className={`text-sm uppercase tracking-wide font-bold mb-4 ${isEnrolled ? 'text-gray-warm' : 'text-gray-warm'}`} data-id="element-899">
                    {tier.sub}
                  </p>
                  <p className={isEnrolled ? 'text-gray-warm' : 'text-gray-warm'} data-id="element-900">
                    {tier.copy}
                  </p>
                </div>;
          })}
          </div>

          <div className="max-w-3xl mx-auto bg-off-white rounded-2xl p-6 border border-subtle-gray flex items-start gap-4" data-id="element-901">
            <Wallet className="w-6 h-6 text-storm-red shrink-0 mt-1" data-id="element-902" />
            <div data-id="element-903">
              <p className="font-bold text-ink mb-1" data-id="element-904">
                Daily Payment Option Available
              </p>
              <p className="text-gray-warm" data-id="element-905">
                Can't commit to a full week? You can also pay by the day. Text
                or call{' '}
                <a href={`tel:${CAMP_SEASON.registrationPhone}`} className="font-bold text-storm-red hover:underline" data-id="element-906">
                  {CAMP_SEASON.registrationPhone}
                </a>{' '}
                to ask about daily pricing and availability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* === WHAT TO BRING + DROP-OFF / PICKUP === */}
      <section className="py-24 bg-off-white" data-id="element-907">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-908">
          <div className="grid lg:grid-cols-2 gap-12" data-id="element-909">
            <div data-id="element-910">
              <h2 className="text-4xl text-ink mb-8" data-id="element-911">
                What Campers Should Bring
              </h2>
              <div className="bg-paper rounded-2xl p-8 border border-subtle-gray" data-id="element-912">
                <ul className="flex flex-col gap-4" data-id="element-913">
                  {WHAT_TO_BRING.map((item, idx) => <li key={idx} className="flex items-center gap-4" data-id="element-914">
                      <div className="w-8 h-8 bg-storm-red rounded-lg flex items-center justify-center shrink-0" data-id="element-915">
                        <CheckCircle2 className="w-5 h-5 text-white" data-id="element-916" />
                      </div>
                      <span className="text-ink font-bold" data-id="element-917">{item}</span>
                    </li>)}
                </ul>
                <div className="mt-6 pt-6 border-t border-subtle-gray flex items-start gap-3" data-id="element-918">
                  <Shirt className="w-5 h-5 text-storm-red shrink-0 mt-0.5" data-id="element-919" />
                  <p className="text-sm text-gray-warm" data-id="element-920">
                    <span className="font-bold text-ink" data-id="element-921">
                      No need to bring a uniform.
                    </span>{' '}
                    a Storm Taekwondo camp shirt is included for every camper.
                  </p>
                </div>
              </div>
            </div>

            <div data-id="element-922">
              <h2 className="text-4xl text-ink mb-8" data-id="element-923">Drop-Off & Pickup</h2>
              <div className="flex flex-col gap-4" data-id="element-924">
                <div className="bg-ink text-white p-6 rounded-2xl" data-id="element-925">
                  <div className="flex items-center gap-3 mb-2" data-id="element-926">
                    <Clock className="w-6 h-6 text-storm-red" data-id="element-927" />
                    <h3 className="text-xl text-white m-0" data-id="element-928">Camp Hours</h3>
                  </div>
                  <p className="text-gray-warm" data-id="element-929">{CAMP_SEASON.hours}</p>
                </div>

                <div className="bg-storm-red text-white p-6 rounded-2xl" data-id="element-930">
                  <div className="flex items-center gap-3 mb-2" data-id="element-931">
                    <AlertCircle className="w-6 h-6 text-white" data-id="element-932" />
                    <h3 className="text-xl text-white m-0" data-id="element-933">
                      No Early Drop-Off
                    </h3>
                  </div>
                  <p className="text-white/90" data-id="element-934">
                    Please arrive at the start of the camp day. Early drop-off
                    is not available.
                  </p>
                </div>

                <div className="bg-charcoal text-white p-6 rounded-2xl" data-id="element-935">
                  <div className="flex items-center gap-3 mb-2" data-id="element-936">
                    <CheckCircle2 className="w-6 h-6 text-storm-red" data-id="element-937" />
                    <h3 className="text-xl text-white m-0" data-id="element-938">
                      Late Pickup Is OK
                    </h3>
                  </div>
                  <p className="text-gray-warm" data-id="element-939">
                    Running late? No problem. Kids can hang out until parents
                    arrive. Regular Storm classes start immediately after camp.
                  </p>
                </div>

                <div className="bg-ink text-white p-6 rounded-2xl" data-id="element-940">
                  <div className="flex items-center gap-3 mb-2" data-id="element-941">
                    <Users className="w-6 h-6 text-storm-red" data-id="element-942" />
                    <h3 className="text-xl text-white m-0" data-id="element-943">
                      {CAMP_SEASON.ages}
                    </h3>
                  </div>
                  <p className="text-gray-warm" data-id="element-944">
                    Camp is open to all kids ages 6 and up. No Taekwondo
                    experience needed.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === WHY PARENTS CHOOSE STORM === */}
      <section className="py-24 bg-paper border-y border-subtle-gray" data-id="element-945">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-946">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-947">
            <h2 className="text-4xl md:text-5xl text-ink mb-6" data-id="element-948">
              Why Parents Choose Storm Summer Camp
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-949">
            {WHY_STORM.map((reason, idx) => <div key={idx} className="bg-off-white p-8 rounded-2xl border border-subtle-gray hover:shadow-lg transition-all" data-id="element-950">
                <reason.icon className="w-10 h-10 text-storm-red mb-4" data-id="element-951" />
                <h3 className="text-2xl text-ink mb-3" data-id="element-952">{reason.title}</h3>
                <p className="text-gray-warm leading-relaxed" data-id="element-953">{reason.copy}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* === REVIEWS === */}
      <section className="py-24 bg-off-white" data-id="element-954">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-955">
          <h2 className="text-4xl md:text-5xl text-ink text-center mb-16" data-id="element-956">
            What Families Say About Storm Taekwondo
          </h2>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-957">
            <ReviewCard quote="My son has been going here for 6 months and his confidence has skyrocketed. The coaches are amazing with the kids." name="Sarah M." label="Parent Review" data-id="element-958" />
            <ReviewCard quote="Great family atmosphere. We started in the family class and it's been the best decision we've made for our health." name="David R." label="Parent Review" data-id="element-959" />
            <ReviewCard quote="The discipline and focus my daughter has learned here translates directly to her schoolwork. Highly recommend Storm!" name="Jennifer L." label="Parent Review" data-id="element-960" />
          </div>
        </div>
      </section>

      {/* === LOCATION === */}
      <section className="py-24 bg-paper border-y border-subtle-gray" data-id="element-961">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-962">
          <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-963">
            <h2 className="text-4xl md:text-5xl text-ink mb-6" data-id="element-964">
              Summer Camp in Farmington Hills, MI
            </h2>
            <p className="text-xl text-gray-warm" data-id="element-965">
              Storm Taekwondo is located in Farmington Hills and serves families
              from nearby communities including Novi, Commerce, Walled Lake,
              Wixom, Livonia, Farmington, Waterford and Northville.
            </p>
          </div>
          <LocationBlock data-id="element-966" />
        </div>
      </section>

      {/* === FAQ === */}
      <section className="py-24 bg-off-white" data-id="element-967">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-968">
          <h2 className="text-4xl md:text-5xl text-ink text-center mb-16" data-id="element-969">
            Summer Camp Questions
          </h2>
          <FAQAccordion faqs={FAQS} data-id="element-970" />
        </div>
      </section>

      {/* === FINAL CTA - CUSTOM (uses camp registration phone, not main phone) === */}
      <section className="relative py-24 overflow-hidden bg-ink" data-id="element-971">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-charcoal transform skew-x-12 translate-x-20 opacity-50 pointer-events-none" data-id="element-972" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-973">
          <h2 className="text-4xl md:text-5xl text-white mb-6" data-id="element-974">
            Ready to Save a Summer Camp Spot?
          </h2>
          <p className="text-xl text-gray-warm mb-10 max-w-2xl mx-auto" data-id="element-975">
            Give your child a structured, active and fun summer experience at
            Storm Taekwondo. Text or call Megan directly to reserve your week.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-976">
            <a href={`tel:${CAMP_SEASON.registrationPhone}`} className="btn-primary w-full sm:w-auto" data-id="element-977">
              <Phone className="w-5 h-5 mr-2" data-id="element-978" />
              Call {CAMP_SEASON.registrationPhone}
            </a>
            <a href={`sms:${CAMP_SEASON.registrationPhone.replace(/-/g, '')}`} className="btn-outline border-gray-warm text-white hover:bg-white hover:text-ink w-full sm:w-auto" data-id="element-979">
              <MessageSquare className="w-5 h-5 mr-2" data-id="element-980" />
              Text To Register
            </a>
          </div>
          <p className="text-gray-warm text-sm mt-8" data-id="element-981">
            Or email{' '}
            <a href={`mailto:${CAMP_SEASON.registrationEmail}`} className="text-storm-red hover:underline font-bold" data-id="element-982">
              {CAMP_SEASON.registrationEmail}
            </a>
          </p>
        </div>
      </section>

      <div className="bg-ink py-6 text-center border-t border-charcoal" data-id="element-983">
        <p className="text-gray-warm text-sm" data-id="element-984">
          Serving Farmington Hills, Novi, Commerce, Livonia and nearby Southeast
          Michigan families.
        </p>
      </div>
    </div>;
}